<template>
  <div>
    <div class="page-header">
      <div class="page-header-left">
        <ol class="breadcrumb ml-0">
          <li class="breadcrumb-item">
            <router-link :to="{ name: 'welcome' }">
              <feather type="home" />
            </router-link>
          </li>
          <li class="breadcrumb-item active">
            {{ $t('userPermissions.title') }}
          </li>
        </ol>
      </div>
      <h4 class="my-3">
        {{ $t('userPermissions.title') }}
      </h4>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <div class="d-flex justify-content-between">
              <div class="lead">
                {{ $t('userPermissions.usersList') }}
              </div>
              <div>
                <button 
                  class="btn btn-outline-secondary" 
                  :disabled="loading"
                  @click="fetchPermissionsData"
                >
                  <i
                    class="fa fa-refresh"
                    :class="{ 'fa-spin': loading }"
                  />
                  {{ $t('refresh') }}
                </button>
              </div>
            </div>
          </div>
          <div class="card-body">
            <p>{{ $t('userPermissions.description') }}</p>

            <div
              v-if="loading"
              class="text-center my-5"
            >
              <div
                class="spinner-border"
                role="status"
              >
                <span class="sr-only">Loading...</span>
              </div>
            </div>

            <div v-else>
              <div
                v-if="Object.keys(allUserPermissions).length === 0"
                class="alert alert-info"
              >
                {{ $t('userPermissions.noUsers') }}
              </div>
              <div
                v-else-if="allPermissions.length === 0"
                class="alert alert-info"
              >
                {{ $t('userPermissions.noPermissions') }}
              </div>
              <div v-else>
                <!-- Single save button for all changes -->
                <div class="mb-3">
                  <button 
                    class="btn btn-primary"
                    :disabled="saving || !hasAnyChanges()"
                    @click="saveAllPermissions()"
                  >
                    <span v-if="saving">
                      <i class="fa fa-spinner fa-spin mr-1" />
                    </span>
                    {{ $t('companyVendors.vendorDetails.save') }}
                  </button>
                </div>

                <div class="table-responsive">
                  <table class="table table-bordered permission-table">
                    <thead>
                      <tr>
                        <th>{{ $t('userPermissions.permission') }}</th>
                        <th
                          v-for="(user, userName) in allUserPermissions"
                          :key="userName"
                        >
                          {{ userName }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="permission in allPermissions"
                        :key="permission.id"
                      >
                        <td>
                          <div class="permission-name">
                            {{ permission.name }}
                          </div>
                        </td>
                        <td
                          v-for="(user, userName) in allUserPermissions"
                          :key="userName"
                        >
                          <div class="checkbox checkbox-success ">
                            <b-form-checkbox
                              :id="'permission-' + permission.id + '-user-' + user.companyUserId"
                              v-model="selectedPermissions[user.companyUserId]"
                              :value="permission.id"
                              @change="logCheckboxChange(user.companyUserId, permission.id)"
                            />
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Add another save button at the bottom for better visibility -->
                <div class="mt-4 text-right">
                  <button 
                    class="btn btn-primary"
                    :disabled="saving || !hasAnyChanges()"
                    @click="saveAllPermissions()"
                  >
                    <span v-if="saving">
                      <i class="fa fa-spinner fa-spin mr-1" />
                    </span>
                    {{ $t('companyVendors.vendorDetails.save') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { getUserPermissions, setPermission } from "@/repositories/GeneralDataRepository";

export default {
  name: 'UserPermissions',
  data() {
    return {
      allUserPermissions: {},
      allPermissions: [],
      loading: false,
      saving: false,
      selectedPermissions: {},
      originalPermissions: {}
    }
  },
  computed: {
    ...mapState({
      token: state => state.auth.token,
      user: state => state.auth.user,
      isAdmin: state => state.auth.isAdmin,
    }),
  },
  mounted() {
    this.fetchPermissionsData();
  },
  methods: {
    async fetchPermissionsData() {
      this.loading = true;
      try {
        const data = await getUserPermissions(this.token);

          this.allUserPermissions = data.allUserPermissions || {};
          this.allPermissions = data.allPermissions || [];

          // Initialize selectedPermissions with current permissions
          this.selectedPermissions = {};
          this.originalPermissions = {};

          Object.keys(this.allUserPermissions).forEach(userName => {
            const user = this.allUserPermissions[userName];
            const userId = user.companyUserId;
            // Extract just the permission IDs for the checkboxes to work correctly
            this.selectedPermissions[userId] = (user.permissions || []).map(permission => permission.id);
            this.originalPermissions[userId] = (user.permissions || []).map(permission => permission.id);
          });

      } catch (error) {
        console.error('Error fetching permissions data:', error);
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: this.$t('errorOccurred')
        });
      } finally {
        this.loading = false;
      }
    },


    async saveAllPermissions() {
      this.saving = true;
      try {
        // Get all users with changes
        const usersWithChanges = Object.keys(this.selectedPermissions).filter(userId => this.hasChanges(userId));

        if (usersWithChanges.length === 0) {
          this.$swal({
            icon: 'info',
            title: this.$t('info'),
            text: this.$t('noChangesToSave')
          });
          return;
        }

        // Prepare the data in the new format
        const userPermissionsData = usersWithChanges.map(userId => {
          // Ensure we're only sending the IDs, not complex objects
          let permissionIds = this.selectedPermissions[userId] || [];
          permissionIds = permissionIds.map(item => typeof item === 'object' && item !== null ? item.id : item);

          return {
            companyUserId: parseInt(userId),
            permissionIds: permissionIds
          };
        });

        // Send all changes in a single request
        const { code, message } = await setPermission(this.token, userPermissionsData);

        if (code === 200) {
          // Update original permissions after successful save
          usersWithChanges.forEach(userId => {
            this.originalPermissions[userId] = [...this.selectedPermissions[userId]];
          });

          this.$swal({
            icon: 'success',
            title: this.$t('success'),
            text: this.$t('successfullySaved')
          });

          // Refresh data to ensure we have the latest permissions
          await this.fetchPermissionsData();
        } else {
          this.$swal({
            icon: 'error',
            title: this.$t('error'),
            text: message || this.$t('errorOccurred')
          });
        }
      } catch (error) {
        console.error('Error saving all permissions:', error);
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: this.$t('errorOccurred')
        });
      } finally {
        this.saving = false;
      }
    },

    hasChanges(userId) {
      if (!this.originalPermissions[userId] || !this.selectedPermissions[userId]) {
        return false;
      }

      if (this.originalPermissions[userId].length !== this.selectedPermissions[userId].length) {
        (`User ${userId} has different permission count: original=${this.originalPermissions[userId].length}, selected=${this.selectedPermissions[userId].length}`);
        return true;
      }

      // Check for permissions that have been added
      const addedPermissions = this.selectedPermissions[userId].filter(id => 
        !this.originalPermissions[userId].includes(id)
      );
      if (addedPermissions.length > 0) {
        (`User ${userId} has added permissions:`, addedPermissions);
        return true;
      }

      // Check for permissions that have been removed
      const removedPermissions = this.originalPermissions[userId].filter(id => 
        !this.selectedPermissions[userId].includes(id)
      );
      if (removedPermissions.length > 0) {
        (`User ${userId} has removed permissions:`, removedPermissions);
        return true;
      }

      return false;
    },

    hasAnyChanges() {
      const result = Object.keys(this.selectedPermissions).some(userId => this.hasChanges(userId));
      ('hasAnyChanges called, result:', result);
      return result;
    },

    logCheckboxChange(userId, permissionId) {
      (`Checkbox changed for user ${userId}, permission ${permissionId}`);
      ('Current selectedPermissions for this user:', this.selectedPermissions[userId]);
      ('Original permissions for this user:', this.originalPermissions[userId]);
      // Force re-evaluation of hasAnyChanges
      this.$forceUpdate();
    }
  }
}
</script>

<style scoped>
.permission-table {
  margin-top: 20px;
}

.permission-name {
  font-weight: bold;
  margin-bottom: 5px;
}
</style>
